import { createSlice } from "@reduxjs/toolkit";

const initialState: any = {
  openProduct: false,
  openPayment: false,
  product: {
    productName: "",
    productId: "",
    scale: "",
    totalQty: "",
    totalPrice: "",
    batchNo: "",
    manualbatchId: "",
    mfd: "",
    expd: "",
  },
  updateProduct: {},
  products: [],
  payment: {
    discount: 0,
    card: 0,
    cash: 0,
    bank: 0,
  },
  updatePayments: {},
  payments: [],
  supplierId: "",
  invoiceNumber: "",
  date: "",
};

const toNumber = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toTwoDecimalString = (value: any) => toNumber(value).toFixed(2);

const toTwoDecimalNumber = (value: any) => Number(toTwoDecimalString(value));

// Helper function to apply FIFO logic on frontend (similar to BatchDetailsPopup)
function applyFrontendFIFOLogic(products: any[], stockData: any[] = []) {
  // If no stock data provided, mark all products as fully editable (nothing sold yet)
  if (!stockData || stockData.length === 0) {
    return products.map((p) => ({
      ...p,
      isEditable: true,
      soldQuantity: 0,
      availableQuantity: Number(p.quantity) || 0,
      isFullySold: false,
      fifoStatus: "Upcoming",
    }));
  }

  // Group products by itemId to apply FIFO per item
  const itemGroups: { [key: string]: any[] } = {};

  products.forEach((product) => {
    const itemId = product.itemId;
    if (!itemGroups[itemId]) {
      itemGroups[itemId] = [];
    }
    itemGroups[itemId].push(product);
  });

  // Process each item group with FIFO logic
  const processedProducts: any[] = [];

  Object.keys(itemGroups).forEach((itemId) => {
    const itemProducts = itemGroups[itemId];

    // Sort by creation date (FIFO - oldest first)
    const sortedProducts = itemProducts.sort(
      (a, b) =>
        new Date(a.createdAt || 0).getTime() -
        new Date(b.createdAt || 0).getTime(),
    );

    // Find stock data for this item (compare as strings — Object.keys returns strings)
    const itemStock = stockData.find(
      (stock) => String(stock.itemId) === String(itemId),
    );

    // Calculate total purchased quantity for this item
    const totalPurchasedQty = sortedProducts.reduce(
      (sum, product) => sum + Number(product.quantity || 0),
      0,
    );

    // If item not in stockData, its stock is 0 (fully depleted/sold)
    const totalAvailableStock = Number(itemStock?.quantity || 0);

    // Calculate total sold quantity
    const totalSoldQty = Math.max(0, totalPurchasedQty - totalAvailableStock);

    let remainingSoldUnits = totalSoldQty;

    // Apply FIFO logic to determine editability
    sortedProducts.forEach((product) => {
      const purchasedQty = Number(product.quantity) || 0;

      // Calculate how many units were sold from this batch using FIFO
      let soldFromBatch = 0;
      if (remainingSoldUnits > 0) {
        soldFromBatch = Math.min(purchasedQty, remainingSoldUnits);
        remainingSoldUnits -= soldFromBatch;
      }

      // Available quantity in this batch
      const availableQty = purchasedQty - soldFromBatch;

      // Determine if editable - only if no units from this batch have been sold
      const isEditable = soldFromBatch === 0;

      // Determine FIFO status
      let fifoStatus = "Depleted";
      if (soldFromBatch === 0) {
        fifoStatus = "Upcoming";
      } else if (soldFromBatch < purchasedQty) {
        fifoStatus = "Current";
      }

      // Update product with calculated values
      product.isEditable = isEditable;
      product.soldQuantity = soldFromBatch;
      product.availableQuantity = availableQty;
      product.isFullySold = soldFromBatch >= purchasedQty;
      product.fifoStatus = fifoStatus;

      processedProducts.push(product);
    });
  });

  return processedProducts;
}

export const purchaseSlice = createSlice({
  name: "purchase",
  initialState,
  reducers: {
    purchaseInput: (state, action) => {
      const { name, value } = action.payload;

      if (name === "quantity") {
        const currentScale =
          state.products?.[state.currentProductIndex]?.scale || state.scale;
        const isDecimalAllowed = currentScale !== "Unit";

        // Validate input based on scale
        const regex = isDecimalAllowed ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;

        if (value && !regex.test(value)) {
          return; // Invalid input, don't update
        }
      }

      if (action.payload.type === "purchase") {
        return {
          ...state,
          [action.payload.name]: action.payload.value,
        };
      } else if (action.payload.type === "payment") {
        return {
          ...state,
          payment: {
            ...state.payment,
            [action.payload.name]: action.payload.value,
          },
        };
      } else if (action.payload.type === "item") {
        if (action.payload.name === "unitPrice") {
          const totalQty = toNumber(state.product.totalQty);
          const unitPrice = action.payload.value;
          const totalPrice = toTwoDecimalString(totalQty * toNumber(unitPrice));

          return {
            ...state,
            product: {
              ...state.product,
              [action.payload.name]: unitPrice,
              totalPrice,
            },
          };
        } else if (action.payload.name === "totalPrice") {
          const totalQty = toNumber(state.product.totalQty);
          const totalPrice = action.payload.value;
          const unitPrice =
            totalQty > 0
              ? toTwoDecimalString(toNumber(totalPrice) / totalQty)
              : "";

          return {
            ...state,
            product: {
              ...state.product,
              [action.payload.name]: totalPrice,
              unitPrice,
            },
          };
        } else {
          return {
            ...state,
            product: {
              ...state.product,
              [action.payload.name]: action.payload.value,
            },
          };
        }
      } else {
        return {
          ...state,
          [action.payload.name]: action.payload.value,
        };
      }
    },

    productInput: (state, action) => {
      return {
        ...state,
        product: {
          ...state.product,
          [action.payload.name]: action.payload.value,
        },
      };
    },
    setProduct: (state, action) => {
      let product = { ...action.payload };

      // If unitPrice is missing but totalPrice and totalQty are present, calculate unitPrice
      if (
        (product.unitPrice === undefined ||
          product.unitPrice === null ||
          product.unitPrice === "") &&
        product.totalPrice &&
        product.totalQty
      ) {
        const qty = Number(product.totalQty);
        const total = Number(product.totalPrice);
        product.unitPrice = qty > 0 ? toTwoDecimalString(total / qty) : "";
      }

      return {
        ...state,
        product,
      };
    },
    // addProduct: (state) => {
    //   const randomSixDigit = Math.floor(100000 + Math.random() * 900000);
    //   const id = randomSixDigit * (state.products?.length + 1);
    //   return {
    //     ...state,
    //     products: [...state.products, { ...state.product, id, isNew: true }],
    //   };
    // },

    //   const newBatchNumber = Number(state.batchNumber) + state.products?.length + 1;

    //   const newProduct = { ...state.product, id, isNew: true, batchNo: newBatchNumber };
    //   return {
    //     ...state,
    //     products: Array.isArray(state.products)
    //       ? [...state.products, newProduct]
    //       : [newProduct], // fallback if state.products is not an array
    //   };
    // },

    addProduct: (state, action) => {
      const incomingBatchNumber = action.payload; // get it directly
      const randomSixDigit = Math.floor(100000 + Math.random() * 900000);
      const id = randomSixDigit * ((state.products?.length || 0) + 1);

      const parsedBatch = Number(incomingBatchNumber);
      const safeBatchBase = Number.isFinite(parsedBatch) ? parsedBatch : 0;
      const newBatchNumber =
        safeBatchBase + (state.products?.length || 0) + 1;

      const newProduct = {
        ...state.product,
        id,
        isNew: true,
        batchNo: newBatchNumber,
        totalPrice: toTwoDecimalString(
          toNumber(state.product?.unitPrice) * toNumber(state.product?.totalQty),
        ),
      };

      if (!Array.isArray(state.products)) {
        state.products = [];
      }

      state.products.unshift(newProduct);
    },

    updateBackendData: (state, action) => {
      const data = action.payload;

      // Extract core purchase info
      state.supplierId = data?.products?.[0]?.supplierId || "";
      state.invoiceNumber = data.invoiceNumber || "";

      state.date = data.invoiceDate
        ? new Date(data.invoiceDate).toISOString().slice(0, 10)
        : "";

      // Apply FIFO logic on frontend to determine editability
      const processedProducts = applyFrontendFIFOLogic(
        data.products || [],
        data.stockData || [],
      );

      const toDateInputValue = (value: any) => {
        if (!value) return "";
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime())
          ? ""
          : parsed.toISOString().slice(0, 10);
      };

      // Set product list with FIFO-based editability
      state.products = processedProducts.map((p: any) => {
        const totalQty = p.quantity;
        const totalPrice = p.totalPrice;
        const qtyNum = toNumber(totalQty);
        const unitPrice =
          qtyNum > 0 ? toTwoDecimalString(toNumber(totalPrice) / qtyNum) : "";

        return {
          id: p.id,
          productId: p.itemId,
          productName: p["Item.itemName"] || "",
          product: p.itemId
            ? `${p.itemId}/${p["Item.itemName"] || ""}/${p["Item.scale"] || ""}`
            : "",
          scale: p["Item.scale"] || "",
          totalQty,
          totalPrice,
          unitPrice,
          batchNo: p.batchNumberId,
          batchNumberId: p.batchNumberId,
          manualbatchId: p.manualbatchId || "",
          mfd: toDateInputValue(p.mfd),
          expd: toDateInputValue(p.expd),
          isNew: false,
          isEditable: p.isEditable || false,
          isEdited: false, // Initialize as false for existing products
          availableStock: p.availableQuantity || 0,
          soldQuantity: p.soldQuantity || 0,
          isFullySold: p.isFullySold || false,
          fifoStatus: p.fifoStatus || "unknown",
          originalData: {
            totalQty: p.quantity,
            totalPrice: p.totalPrice,
            mfd: toDateInputValue(p.mfd),
            expd: toDateInputValue(p.expd),
          },
        };
      });

      // Set payments with editable logic
      state.payments = (data.payments || []).map((pay: any) => ({
        id: pay.id,
        discount: pay.discount,
        card: pay.card,
        cash: pay.cash,
        bank: pay.bank,
        total: pay.total,
        netTotal: pay.netTotal,
        due: pay.due,
        isNew: false,
        isEditable: true,
        isEdited: false, // Initialize as false for existing payments
        originalData: {
          discount: pay.discount,
          card: pay.card,
          cash: pay.cash,
          bank: pay.bank,
        },
      }));

      // Reset nested form drafts so add/edit after reset works
      state.product = { ...initialState.product };
      state.payment = { ...initialState.payment };
      state.openProduct = false;
      state.openPayment = false;
    },

    updateProduct: (state, action) => {
      const updatedProduct = action.payload;
      return {
        ...state,
        products: state.products.map((item: any) =>
          String(item.id) === String(updatedProduct.id)
            ? {
                ...item,
                ...updatedProduct,
                isEdited: !item.isNew, // Only mark as edited if it's NOT new
              }
            : item,
        ),
        // Clear the updateProduct state after updating
        updateProduct: {},
      };
    },

    deleteProduct: (state, action) => {
      return {
        ...state,
        products: state.products.filter(
          (item: any) => String(item.id) !== String(action.payload),
        ),
      };
    },
    toogleProduct: (state, action) => {
      return {
        ...state,
        openProduct: action.payload,
      };
    },
    paymentInput: (state, action) => {
      return {
        ...state,
        payment: {
          ...state.payment,
          [action.payload.name]: action.payload?.value || 0,
        },
      };
    },
    setPayment: (state, action) => {
      return {
        ...state,
        payment: action.payload,
      };
    },
    addPayment: (state, action) => {
      if (!Array.isArray(state.payments)) {
        state.payments = [];
      }

      // Use the ID from the action payload or generate a random one
      const paymentId =
        action.payload.id || Math.floor(100000 + Math.random() * 900000);

      // Ensure all numeric values are handled correctly
      const payment = {
        ...state.payment,
        id: paymentId,
        total: toTwoDecimalNumber(action.payload.result),
        due: toTwoDecimalNumber(action.payload.due),
        netTotal: toTwoDecimalNumber(action.payload.netTotal),
        discount: toTwoDecimalNumber(state.payment.discount),
        card: toTwoDecimalNumber(state.payment.card),
        cash: toTwoDecimalNumber(state.payment.cash),
        bank: toTwoDecimalNumber(state.payment.bank),
        isNew: true,
      };

      state.payments.push(payment);
    },

    updatePayment: (state, action) => {
      const updatedPayment = action.payload;

      // Make sure we're finding the right payment by ID
      if (!updatedPayment.id) {
        console.error("Cannot update payment: no id provided", updatedPayment);
        return state;
      }

      const paymentExists = state.payments.some(
        (item: any) => String(item.id) === String(updatedPayment.id),
      );

      if (!paymentExists) {
        console.error("Cannot update payment: id not found", updatedPayment.id);
        return state;
      }

      // Ensure all payment values are numbers to prevent calculation issues
      const sanitizedPayment = {
        ...updatedPayment,
        discount: toTwoDecimalNumber(updatedPayment.discount),
        card: toTwoDecimalNumber(updatedPayment.card),
        cash: toTwoDecimalNumber(updatedPayment.cash),
        bank: toTwoDecimalNumber(updatedPayment.bank),
        total: toTwoDecimalNumber(updatedPayment.total),
        due: toTwoDecimalNumber(updatedPayment.due),
        netTotal: toTwoDecimalNumber(updatedPayment.netTotal),
      };

      return {
        ...state,
        payments: state.payments.map((item: any) =>
          String(item.id) === String(sanitizedPayment.id)
            ? {
                ...item,
                ...sanitizedPayment,
                isEdited: !item.isNew, // Mark as edited if it's not new
              }
            : item,
        ),
      };
    },
    deletePayment: (state, action) => {
      return {
        ...state,
        payments: state.payments.filter(
          (item: any) => String(item.id) !== String(action.payload),
        ),
      };
    },
    tooglePayment: (state, action) => {
      return {
        ...state,
        openPayment: action.payload,
      };
    },
    clearProduct: (state) => {
      return {
        ...state,
        product: { ...initialState.product },
      };
    },
    clearPayment: (state) => {
      return {
        ...state,
        payment: { ...initialState.payment },
      };
    },

    submitPurchase: () => {
      return {
        ...initialState,
        product: { ...initialState.product },
        payment: { ...initialState.payment },
        products: [],
        payments: [],
      };
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  purchaseInput,
  addProduct,
  toogleProduct,
  productInput,
  setProduct, // Make sure this is exported
  updateProduct,
  deleteProduct,
  addPayment,
  tooglePayment,
  paymentInput,
  setPayment,
  updatePayment,
  deletePayment,
  submitPurchase,
  updateBackendData,
  clearProduct,
  clearPayment,
} = purchaseSlice.actions;

export default purchaseSlice.reducer;
